/**
 * useDrillThrough — Phase 5.8 click-to-filter helper.
 *
 * Returns a function `drillTo(field, key, extraFilters?)` that navigates to
 * /tickets pre-filtered to the clicked dimension. The TicketsList page reads
 * standard query params (department_id, owner_email, status, priority,
 * channel, date_from, date_to).
 *
 * Field aliases (engine `group_by.field` → /tickets query param):
 *   department_name → department_id (we keep both forms; we resolve to id
 *                                     when the row carries it)
 *   owner_email     → owner_email
 *   status / priority / channel → same
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

const ENGINE_TO_TICKETS_PARAM = {
  status: "status",
  priority: "priority",
  channel: "channel",
  owner_email: "owner_email",
  department_id: "department_id",
  department_name: "department_name",
};

export function useDrillThrough() {
  const navigate = useNavigate();
  return useCallback((field, key, extras = {}) => {
    const params = new URLSearchParams();
    const mapped = ENGINE_TO_TICKETS_PARAM[field] || field;
    if (key !== undefined && key !== null && key !== "") {
      params.set(mapped, String(key));
    }
    for (const [k, v] of Object.entries(extras || {})) {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    navigate(qs ? `/tickets?${qs}` : "/tickets");
  }, [navigate]);
}

/** Quick formatter for the comparison delta badge. */
export function formatDelta(pct) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct}%`;
}

/** Returns `'up' | 'down' | 'flat' | null` for icon/colour decisions. */
export function deltaDirection(pct) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  if (pct > 0) return "up";
  if (pct < 0) return "down";
  return "flat";
}
